import { SCRIPT_MAX_BYTES, VERSION_LABEL_MAX } from '@noconflict/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { orderErrorMessage } from '../../../entities/order';
import { uploadScriptObject } from '../../../entities/project-file';
import { scriptVersionKeys, uploadVersion } from '../../../entities/script-version';
import { ApiRequestError } from '../../../shared/api/http';
import { supabase } from '../../../shared/api/supabase';

/** Why a version was turned away: about the file or the order, so the same file is not offered again. */
export type VersionRefusal = 'not_pdf' | 'too_large' | 'unpriceable' | 'limit' | 'label';

export type UploadVersionState =
  | { phase: 'idle' }
  | { phase: 'uploading' }
  | { phase: 'reading' }
  | { phase: 'refused'; reason: VersionRefusal }
  /** Something of ours failed (the network, the extractor): the same file can be tried again. */
  | { phase: 'failed'; message: string };

const REFUSALS: Record<string, VersionRefusal> = {
  script_unreadable: 'unpriceable',
  script_too_long: 'unpriceable',
  not_a_pdf: 'not_pdf',
  invalid_file: 'not_pdf',
  file_too_large: 'too_large',
  version_limit_reached: 'limit',
  invalid_label: 'label',
};

const looksLikePdf = (file: File) =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

/**
 * Uploads a version of an ordered script and waits while it is measured: the PDF goes to storage under a fresh
 * id with the customer's own token, then the worker registers, measures and numbers it. The versions are then
 * refetched. A file that is no use, or a full order, is refused with a reason; a failure of ours keeps the file
 * so it can be tried again.
 */
export function useUploadVersion(projectId: string) {
  const qc = useQueryClient();
  const [state, setState] = useState<UploadVersionState>({ phase: 'idle' });
  const last = useRef<{ file: File; label: string } | null>(null);

  const upload = useCallback(
    async (file: File, rawLabel: string): Promise<boolean> => {
      const label = rawLabel.trim();
      last.current = { file, label };
      if (label.length > VERSION_LABEL_MAX) {
        setState({ phase: 'refused', reason: 'label' });
        return false;
      }
      if (!looksLikePdf(file)) {
        setState({ phase: 'refused', reason: 'not_pdf' });
        return false;
      }
      if (file.size > SCRIPT_MAX_BYTES) {
        setState({ phase: 'refused', reason: 'too_large' });
        return false;
      }
      try {
        setState({ phase: 'uploading' });
        const fileId = crypto.randomUUID();
        await uploadScriptObject(supabase, projectId, fileId, file);
        setState({ phase: 'reading' });
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;
        if (!token) throw new Error('forbidden');
        await uploadVersion(token, projectId, {
          fileId,
          filename: file.name,
          ...(label ? { label } : {}),
        });
        await qc.invalidateQueries({ queryKey: scriptVersionKeys.all });
        setState({ phase: 'idle' });
        return true;
      } catch (e) {
        const reason = e instanceof ApiRequestError ? REFUSALS[e.code] : undefined;
        setState(
          reason
            ? { phase: 'refused', reason }
            : { phase: 'failed', message: orderErrorMessage(e) },
        );
        return false;
      }
    },
    [projectId, qc],
  );

  /** Tries the last file again, after a failure that was not about the file. */
  const retry = useCallback(
    () => (last.current ? upload(last.current.file, last.current.label) : Promise.resolve(false)),
    [upload],
  );

  return { state, upload, retry, busy: state.phase === 'uploading' || state.phase === 'reading' };
}
